import type {
  DataEnvelope,
  PublicInstitution,
  VerificationMethod,
  VerifiedResultPayload,
} from '@/types';
import { api, ApiError } from './api';

export type VerifyOutcome =
  | { status: 'verified'; data: VerifiedResultPayload }
  | { status: 'not_verified' | 'error'; message: string; verificationId: string | null }
  /** Validation failed (422); show the message on the form. */
  | { status: 'invalid'; message: string }
  /** Too many attempts (429). */
  | { status: 'rate_limited'; message: string; retryAfter: number | null }
  /** Maintenance mode is on (503). */
  | { status: 'maintenance'; message: string };

export interface PublicStatus {
  maintenanceMode: boolean;
  /** False in production, where demo reset is off. Missing from older APIs. */
  demoReset?: boolean;
}

export const PUBLIC_STATUS_KEY = ['public', 'status'] as const;

export const publicService = {
  /** Institutions open for verification, sorted by name. */
  async institutions(): Promise<PublicInstitution[]> {
    const { data } = await api.get<DataEnvelope<PublicInstitution[]>>('institutions/public');
    return data;
  },

  status(): Promise<PublicStatus> {
    return api.get<PublicStatus>('status');
  },

  /**
   * Verifies a certificate. Every expected answer, including refusals, comes
   * back as an outcome; only unexpected failures (e.g. no connection) throw.
   */
  async verify(
    institutionId: string,
    method: VerificationMethod,
    reference: string,
  ): Promise<VerifyOutcome> {
    try {
      return await api.post<VerifyOutcome>('verifications', { institutionId, method, reference });
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      if (error.isValidation) {
        const message =
          error.fieldError('reference') ??
          error.fieldError('institutionId') ??
          error.fieldError('method') ??
          error.message;
        return { status: 'invalid', message };
      }
      if (error.isRateLimited) {
        return { status: 'rate_limited', message: error.message, retryAfter: error.retryAfter };
      }
      if (error.isMaintenance) {
        return { status: 'maintenance', message: error.message };
      }
      throw error;
    }
  },
};
